import { useEffect, useState } from "react";
import { ArrowRight, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { APP_VERSION } from "@/lib/version";

const NOTES = [
  ["突然有灵感，修复了一些已知问题", ""],
  ["全局搜索正式上线", "左侧任务栏新增全局搜索，可同时查找普通 Report、Google Review 与两个 Sheet Report 的资料。输入 Code、Outlet、状态或其他记录内容后，点击结果即可自动打开正确报表与月份，并定位高亮对应资料。"],
  ["删除资料更加安心", "Data Ledger 删除的资料会先进入 Trash，并保留 3 天供恢复。RMU Pending 及其自动同步的 RMU Report、Costing Summary 会作为同一组安全处理；跨月带入的资料也可独立删除，不影响上个月记录。"],
  ["批量整理与固定表头", "Data Ledger 现已支持勾选多笔资料后批量移至 Trash；桌面与移动设备滚动长表格时，栏位标题会保持可见，让检查和整理资料更快速。"],
  ["全新台灯登录体验", "登录页已升级为可拉动开关的互动台灯设计；拉一次开灯并显示登录表单，再拉一次即可关灯。浏览器标签页图标也已换成同款台灯 Logo，让整体品牌视觉更加统一。"],
];

export const ReleaseNotesDialog = ({ open, busy, onAcknowledge }) => {
  const [checked, setChecked] = useState(false);
  useEffect(() => { if (open) setChecked(false); }, [open]);

  return (
    <AlertDialog open={open}>
      <AlertDialogContent data-testid="release-notes-dialog" className="max-w-2xl overflow-hidden border-sky-200 p-0 shadow-2xl">
        <div className="border-b border-sky-100 bg-gradient-to-r from-sky-50 via-white to-amber-50 px-6 py-5">
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-amber-400 shadow-sm">
            <Sparkles className="h-5 w-5" />
          </div>
          <AlertDialogHeader className="space-y-1 text-left">
            <AlertDialogTitle data-testid="release-notes-title" className="text-2xl font-extrabold text-slate-900">
              版本 {APP_VERSION} 优化更新
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="release-notes-description" className="text-sm text-slate-600">
              本次更新带来灵感驱动的已知问题修复，并持续优化 PDF 生成、图表分析与资料展示体验。
            </AlertDialogDescription>
          </AlertDialogHeader>
        </div>

        <div className="max-h-[52vh] space-y-3 overflow-y-auto px-6 py-1">
          {NOTES.map(([title, detail], index) => (
            <div key={title} className="flex gap-3 border-b border-slate-100 py-3 last:border-0" data-testid={`release-note-${index + 1}`}>
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              <div>
                <p className="text-sm font-bold text-slate-900">{title}</p>
                <p className="mt-1 text-sm leading-6 text-slate-600">{detail}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-200 bg-slate-50 px-6 py-4">
          <label htmlFor="release-acknowledge" className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
            <Checkbox id="release-acknowledge" checked={checked} onCheckedChange={(value) => setChecked(!!value)}
              data-testid="release-notes-acknowledge-checkbox" className="mt-0.5" />
            <span data-testid="release-notes-acknowledge-label" className="text-sm font-semibold text-slate-700">
              我已阅读并了解版本 {APP_VERSION} 的更新内容
            </span>
          </label>
          <AlertDialogFooter className="mt-4">
            <Button type="button" onClick={onAcknowledge} disabled={!checked || busy}
              data-testid="release-notes-continue-button" className="w-full bg-slate-900 font-bold text-white hover:bg-slate-800 sm:w-auto">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
              {busy ? "正在确认…" : "我已了解，继续使用"}
            </Button>
          </AlertDialogFooter>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};